import {createContext} from 'react';

import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaValidationStatus} from 'schemas/SchemaValidationConstants';

import {SmartChipType} from './SmartChipTypes';

interface BaseChipSnapshotData {
  id: string;
  name?: string | null;
  url?: string | null;
  isInaccessible: boolean;
  isArchived?: boolean | null;
  archivePurpose?: string | null;
  secondaryText?: string | null;
  entityType?: string | null;
}

export interface StorableChipSnapshotData extends BaseChipSnapshotData {
  chipType: SmartChipType.STORABLE;
  storableSchemaIconId?: string | null;
  isExpired?: boolean | null;
  checkOutStatus?: string | null; // CheckOutStatus enum value (not boolean)
  restrictionStatus?: string | null; // RestrictionStatus enum value
  isCurrentUserAuthorized?: boolean | null;
}

export interface EntryChipSnapshotData extends BaseChipSnapshotData {
  chipType: SmartChipType.ENTRY;
  humanId?: string | null;
}

export interface StageEntryChipSnapshotData extends BaseChipSnapshotData {
  chipType: SmartChipType.STAGE_ENTRY;
  humanId?: string | null;
}

export interface StudyChipSnapshotData extends BaseChipSnapshotData {
  chipType: SmartChipType.STUDY;
  humanId?: string | null;
}

export interface WorksheetChipSnapshotData extends BaseChipSnapshotData {
  chipType: SmartChipType.WORKSHEET;
  // Backend sends humanId; the adapter maps this to `displayId` for the WorksheetNode GQL shape.
  humanId?: string | null;
}

export interface BioentityChipSnapshotData extends BaseChipSnapshotData {
  chipType: SmartChipType.BIOENTITY;
  tagSchemaIconId?: string | null;
  sequenceType?: SequenceType | null;
  schemaValidationStatus?: SchemaValidationStatus | null;
  includeRegistryIdInChips?: boolean | null;
}

export interface GenericChipSnapshotData extends BaseChipSnapshotData {
  chipType: Exclude<
    SmartChipType,
    | SmartChipType.STORABLE
    | SmartChipType.ENTRY
    | SmartChipType.BIOENTITY
    | SmartChipType.STAGE_ENTRY
    | SmartChipType.STUDY
    | SmartChipType.WORKSHEET
  >;
}

export type ChipSnapshotData =
  | StorableChipSnapshotData
  | EntryChipSnapshotData
  | BioentityChipSnapshotData
  | StageEntryChipSnapshotData
  | StudyChipSnapshotData
  | WorksheetChipSnapshotData
  | GenericChipSnapshotData;

export const SmartChipSnapshotContext = createContext<ReadonlyMap<string, ChipSnapshotData> | null>(null);
